import { MoneySchema } from "@redotech/money";
import * as z from "zod";

export enum RedoInternalProduct {
  RETURNS = "RETURNS",
  PACKAGE_PROTECTION = "PACKAGE_PROTECTION",
  FINAL_SALE_RETURNS = "FINAL_SALE_RETURNS",
  WARRANTY = "WARRANTY",
  OMS = "OMS",
  WMS = "WMS",
  EMAIL_SMS = "EMAIL_SMS",
  ORDER_TRACKING = "ORDER_TRACKING",
  REVIEW = "REVIEW",
  LOYALTY = "LOYALTY",
  CHECKOUT_OPTIMIZATION = "CHECKOUT_OPTIMIZATION",
  POST_PURCHASE_UPSELL = "POST_PURCHASE_UPSELL",
  CUSTOMER_ACCOUNTS = "CUSTOMER_ACCOUNTS",
  SUPPORT_AI = "SUPPORT_AI",
  IMS = "IMS",
  CATALOG = "CATALOG",
  AEO = "AEO",
  RMR = "RMR",
  LCG = "LCG",
  RECOVER = "RECOVER",
  RECLAIM = "RECLAIM",
  HSA = "HSA",
  XBORDER_RETURNS = "XBORDER_RETURNS",
}

const baseInternalProductSchema = z.object({
  /**
   * Whether the product is enabled for the team (regardless of whether it's
   * being used).
   */
  isEnabled: z.boolean(),
  /**
   * Whether the product is actually being used.
   */
  isActive: z.boolean(),
  /**
   * Whether we are billing the team for the product.
   */
  isBillingActive: z.boolean(),
});

// Derived fields carry a `definitionVersion` (bump on meaning change); the version
// literals + descriptions live in the checkout-optimization-derivations registry.
const checkoutOptimizationSchema = baseInternalProductSchema.extend({
  additionalDetails: z
    .object({
      conversionBillingEnabled: z.boolean(),
      conversionBillingAccepted: z.boolean(),
      last7DaysBilledOrderCount: z.number().nullish(),
      last7DaysBilledOrderRevenue: MoneySchema.nullish(),
      segmentation: z
        .object({ types: z.array(z.string()), definitionVersion: z.number() })
        .nullish(),
      checkoutWidgets: z
        .object({
          activeIds: z.array(z.string()),
          definitionVersion: z.number(),
        })
        .nullish(),
      intentRescuePanel: z
        .object({ isActive: z.boolean(), definitionVersion: z.number() })
        .nullish(),
      dimensionalPackaging: z
        .object({ isActive: z.boolean(), definitionVersion: z.number() })
        .nullish(),
      failedDiscountRescue: z
        .object({
          impactedRevenueTrailing30Day: z.number(),
          recoveredOrdersTrailing30Day: z.number(),
          avgRecoveredOrderValueTrailing30Day: z.number(),
          failedDiscountsDetectedTrailing30Day: z.number(),
          rescuesClaimedTrailing30Day: z.number(),
          claimRateTrailing30Day: z.number(),
          definitionVersion: z.number(),
        })
        .nullish(),
    })
    .nullish(),
});

const redoInternalProductsSchema_ = z.object({
  status: z.object({
    [RedoInternalProduct.RETURNS]: baseInternalProductSchema,
    [RedoInternalProduct.PACKAGE_PROTECTION]: baseInternalProductSchema,
    [RedoInternalProduct.FINAL_SALE_RETURNS]: baseInternalProductSchema,
    [RedoInternalProduct.WARRANTY]: baseInternalProductSchema,
    [RedoInternalProduct.OMS]: baseInternalProductSchema,
    [RedoInternalProduct.WMS]: baseInternalProductSchema,
    [RedoInternalProduct.EMAIL_SMS]: baseInternalProductSchema,
    [RedoInternalProduct.ORDER_TRACKING]: baseInternalProductSchema,
    [RedoInternalProduct.REVIEW]: baseInternalProductSchema,
    [RedoInternalProduct.LOYALTY]: baseInternalProductSchema,
    [RedoInternalProduct.CHECKOUT_OPTIMIZATION]: checkoutOptimizationSchema,
    [RedoInternalProduct.POST_PURCHASE_UPSELL]: baseInternalProductSchema,
    [RedoInternalProduct.CUSTOMER_ACCOUNTS]: baseInternalProductSchema,
    [RedoInternalProduct.SUPPORT_AI]: baseInternalProductSchema,
    [RedoInternalProduct.IMS]: baseInternalProductSchema,
    [RedoInternalProduct.CATALOG]: baseInternalProductSchema,
    [RedoInternalProduct.AEO]: baseInternalProductSchema,
    [RedoInternalProduct.RMR]: baseInternalProductSchema,
    [RedoInternalProduct.LCG]: baseInternalProductSchema,
    [RedoInternalProduct.RECOVER]: baseInternalProductSchema,
    [RedoInternalProduct.RECLAIM]: baseInternalProductSchema,
    [RedoInternalProduct.HSA]: baseInternalProductSchema,
    [RedoInternalProduct.XBORDER_RETURNS]: baseInternalProductSchema,
  }),
  lastSyncedAt: z.coerce.date(),
});

export const redoInternalProductsSchema = redoInternalProductsSchema_;
export interface RedoInternalProducts extends z.infer<
  typeof redoInternalProductsSchema_
> {}

export function isRedoInternalProductEnabled(
  redoInternalProducts: RedoInternalProducts | undefined,
  product: RedoInternalProduct,
): boolean {
  return !!redoInternalProducts?.status?.[product]?.isEnabled;
}

export function isRedoInternalProductActive(
  redoInternalProducts: RedoInternalProducts | undefined,
  product: RedoInternalProduct,
): boolean {
  return !!redoInternalProducts?.status?.[product]?.isActive;
}

export function enabledRedoInternalProducts(
  redoInternalProducts: RedoInternalProducts | undefined,
): Set<RedoInternalProduct> {
  const enabled = new Set<RedoInternalProduct>();
  for (const product of Object.values(RedoInternalProduct)) {
    if (isRedoInternalProductEnabled(redoInternalProducts, product)) {
      enabled.add(product);
    }
  }
  return enabled;
}
